import React, { useCallback, useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import {
    NSFormGroup, NSInput, NSLabel, NSSelect, NSDatePicker, NSCard,
} from 'bricks';
import NSSwitch from 'bricks/NSSwitch/NSSwitch';
import { format, parseISO } from 'date-fns';
import { STATES_OPTIONS } from 'constants/statesList';
import CoordinateInputRow from 'components/CoordinateInputRow/CoordinateInputRow';
import InvestmentMemoDropzone from 'views/InvestmentMemo/InvestmentMemoDropzone/InvestmentMemoDropzone';
import useToast from 'hooks/useToast';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import { ProFormaModelIds } from 'constants/proForma';
import { isStudentHousingModel } from 'helpers/proForma';
import { AISuggestedDocument, TitleData } from '../../../types';
import { ITemporaryUploadedFile } from '../../../../ProFormaUpload/types';
import { AI_ICON_URL } from '../../../constants';
import { useInvestmentMemoContext } from '../../../InvestmentMemoContext';

export interface TitleFormProps {
    data: TitleData;
    onChange: (data: Partial<TitleData>) => void;
    isLoadingTagline?: boolean;
    proFormaModelId?: ProFormaModelIds;
}

const TitleForm = ({
    data,
    onChange,
    isLoadingTagline = false,
    proFormaModelId,
}: TitleFormProps) => {
    const { showError } = useToast();
    const { selectedOrganizationId, userOrganizations } = useAuthContext();
    const {
        documentSuggestions,
        isLoadingDocumentSuggestions,
        shouldShowError,
        getFieldError,
    } = useInvestmentMemoContext();

    const selectedOrganization = userOrganizations.find(org => org.organization.correlationId === selectedOrganizationId);
    const organizationLogoUrl = selectedOrganization?.organization.logoUrl || undefined;

    const parseDate = (dateValue: string): Date | null => {
        if (!dateValue) return null;
        try {
            return parseISO(dateValue);
        } catch {
            return null;
        }
    };

    const handleLogoUploaded = useCallback((file: ITemporaryUploadedFile) => {
        try {
            onChange({ logoFiles: [...data.logoFiles, file] });
        } catch (err) {
            console.error('Failed to handle uploaded logo', err);
            showError('Failed to upload logo');
        }
    }, [onChange, data.logoFiles, showError]);

    const handleLogoDeleted = useCallback((file: ITemporaryUploadedFile) => {
        onChange({ logoFiles: data.logoFiles.filter(f => f.id !== file.id) });
    }, [onChange, data.logoFiles]);

    const handleBuildingExteriorUploaded = useCallback((file: ITemporaryUploadedFile) => {
        try {
            onChange({
                buildingExteriorFiles: {
                    uploaded: [file], // Only one uploaded file allowed
                    suggested: [], // Clear suggested when uploading
                },
            });
        } catch (err) {
            console.error('Failed to handle uploaded building exterior', err);
            showError('Failed to upload building exterior');
        }
    }, [onChange, showError]);

    const handleBuildingExteriorDeleted = useCallback((file: ITemporaryUploadedFile) => {
        onChange({
            buildingExteriorFiles: {
                uploaded: data.buildingExteriorFiles.uploaded.filter(f => f.id !== file.id),
                suggested: data.buildingExteriorFiles.suggested,
            },
        });
    }, [onChange, data.buildingExteriorFiles]);

    const buildingExteriorFilesSelectedIds = useMemo(() => new Set(data.buildingExteriorFiles.suggested.map(f => f.id)),
        [data.buildingExteriorFiles.suggested]);

    return (
        <NSCard className="px-3 pb-3 NSCard--level-2">
            <NSFormGroup>
                <NSInput
                    name="dealName"
                    type="text"
                    label="Deal name"
                    value={data.dealName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ dealName: e.target.value })}
                    required
                    showErrorMsg={shouldShowError('title.dealName')}
                    errorMsg={getFieldError('title.dealName')}
                />
            </NSFormGroup>

            <NSFormGroup>
                <NSLabel>
                    Tagline (optional)
                    {' '}
                    <img
                        src={AI_ICON_URL}
                        className="NSInput__label-ai-icon rounded"
                        alt="Northspyre AI"
                    />
                </NSLabel>
                <RenderIf isTrue={isLoadingTagline}>
                    <div className="d-flex align-items-center justify-content-center p-2">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        <span className="text-muted">Generating tagline...</span>
                    </div>
                </RenderIf>
                <RenderIf isTrue={!isLoadingTagline}>
                    <NSInput
                        name="tagline"
                        type="text"
                        value={data.tagline}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ tagline: e.target.value })}
                    />
                </RenderIf>
            </NSFormGroup>

            <NSFormGroup>
                <NSDatePicker
                    name="date"
                    label="Date"
                    value={parseDate(data.date)}
                    onChange={(date: Date | null) => {
                        if (date) {
                            onChange({ date: format(date, 'yyyy-MM') });
                        } else {
                            onChange({ date: '' });
                        }
                    }}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                    required
                    showErrorMsg={shouldShowError('title.date')}
                    errorMsg={getFieldError('title.date')}
                />
            </NSFormGroup>

            <RenderIf isTrue={!!proFormaModelId && isStudentHousingModel(proFormaModelId)}>
                <NSFormGroup>
                    <NSInput
                        name="associatedUniversity"
                        type="text"
                        label="Associated university"
                        value={data.associatedUniversity}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ associatedUniversity: e.target.value })}
                    />
                </NSFormGroup>
            </RenderIf>

            <RenderIf isTrue={!data.useCoordinates}>
                <NSFormGroup>
                    <NSInput
                        name="address"
                        type="text"
                        label="Address"
                        value={data.address}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ address: e.target.value })}
                        required
                        showErrorMsg={shouldShowError('title.address')}
                        errorMsg={getFieldError('title.address')}
                    />
                </NSFormGroup>

                <Row>
                    <Col md={4}>
                        <NSFormGroup>
                            <NSInput
                                name="city"
                                type="text"
                                label="City"
                                value={data.city}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ city: e.target.value })}
                                required
                                showErrorMsg={shouldShowError('title.city')}
                                errorMsg={getFieldError('title.city')}
                            />
                        </NSFormGroup>
                    </Col>
                    <Col md={4}>
                        <NSFormGroup>
                            <NSLabel required>State</NSLabel>
                            <NSSelect
                                name="state"
                                options={STATES_OPTIONS}
                                value={
                                    // BE stores full state name and to support initial value selection
                                    // we check both value and label
                                    STATES_OPTIONS.find(
                                        option => option.value === data.state || option.label === data.state,
                                    ) || null
                                }
                                onChange={(option: any) => onChange({ state: option?.value || '' })}
                                isClearable={false}
                            />
                            {shouldShowError('title.state') && (
                                <p className="text-danger small mt-1 mb-0">{getFieldError('title.state')}</p>
                            )}
                        </NSFormGroup>
                    </Col>
                    <Col md={4}>
                        <NSFormGroup>
                            <NSInput
                                name="zipCode"
                                type="text"
                                label="Zip code"
                                value={data.zipCode}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ zipCode: e.target.value })}
                                required
                                showErrorMsg={shouldShowError('title.zipCode')}
                                errorMsg={getFieldError('title.zipCode')}
                            />
                        </NSFormGroup>
                    </Col>
                </Row>
            </RenderIf>

            <RenderIf isTrue={data.useCoordinates}>
                <NSFormGroup>
                    <CoordinateInputRow
                        name="latitude"
                        label="Latitude"
                        value={data.latitude}
                        min={-90}
                        max={90}
                        onCommit={value => onChange({ latitude: value })}
                    />
                    {shouldShowError('title.latitude') && (
                        <p className="text-danger small mt-1 mb-0">{getFieldError('title.latitude')}</p>
                    )}
                </NSFormGroup>
                <NSFormGroup>
                    <CoordinateInputRow
                        name="longitude"
                        label="Longitude"
                        value={data.longitude}
                        min={-180}
                        max={180}
                        onCommit={value => onChange({ longitude: value })}
                    />
                    {shouldShowError('title.longitude') && (
                        <p className="text-danger small mt-1 mb-0">{getFieldError('title.longitude')}</p>
                    )}
                </NSFormGroup>
            </RenderIf>

            <NSFormGroup>
                <NSSwitch
                    id="titleUseCoordinates"
                    name="useCoordinates"
                    data-testid="titleUseCoordinatesSwitch"
                    checked={!!data.useCoordinates}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ useCoordinates: e.target.checked })}
                    label="Use coordinates"
                />
            </NSFormGroup>

            <NSFormGroup>
                <NSInput
                    name="sponsor"
                    type="text"
                    label="Sponsor/developer name"
                    value={data.sponsor}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ sponsor: e.target.value })}
                    required
                    showErrorMsg={shouldShowError('title.sponsor')}
                    errorMsg={getFieldError('title.sponsor')}
                />
            </NSFormGroup>

            <InvestmentMemoDropzone
                label="Sponsor/developer logo"
                multiple={false}
                defaultImageUrl={organizationLogoUrl}
                showFileCards={false}
                onUploaded={handleLogoUploaded}
                onDeleted={handleLogoDeleted}
                onDefaultImageDeleted={() => onChange({ logoFiles: [] })}
                filesFromData={data.logoFiles}
            />

            <InvestmentMemoDropzone
                label="High-resolution rendering of building exterior (select one)"
                multiple={false}
                onUploaded={handleBuildingExteriorUploaded}
                onDeleted={handleBuildingExteriorDeleted}
                filesFromData={data.buildingExteriorFiles.uploaded}
                documentLibrarySuggestions={{
                    suggestions: documentSuggestions?.title || [],
                    isLoadingSuggestions: isLoadingDocumentSuggestions,
                    selectedIds: buildingExteriorFilesSelectedIds,
                    onToggle: (doc: AISuggestedDocument, isSelected: boolean) => {
                        if (isSelected && data.buildingExteriorFiles.uploaded.length > 0) {
                            data.buildingExteriorFiles.uploaded.forEach(handleBuildingExteriorDeleted);
                        }

                        onChange({
                            buildingExteriorFiles: {
                                uploaded: [], // Clear uploaded when selecting suggested
                                suggested: !isSelected
                                    ? data.buildingExteriorFiles.suggested.filter(d => d.id !== doc.id)
                                    : [doc], // Only allow one suggested file to be selected
                            },
                        });
                    },
                }}
            />
        </NSCard>
    );
};

export default TitleForm;
